import baseApiClient from '@cnd_fe/ky-client'
import { showAlert } from '@/utils/alert/alertBridge'

// API용 클라이언트 생성
const cmnClient = baseApiClient.extend({
  prefixUrl: import.meta.env.VITE_RES_CMMN_SERVICE_URL
})

// API용 클라이언트 생성
const cmmnOnlyClient = baseApiClient.extend({
  prefixUrl: import.meta.env.VITE_RES_CMMN_ONLY_SERVICE_URL
})

// 결제용 클라이언트 생성
const payClient = baseApiClient.extend({
  prefixUrl: import.meta.env.VITE_RES_PAYMENT_SERVICE_URL
})

/**
 * 사용중인 공통코드 목록 조회
 * @param {String} prntId - 상위코드ID
 * @returns {Promise<any>}
 */
export const fetchCmmCodeList = (prntId) => {
  try {
    const result = cmnClient.post('code-list', { json: { prntId: prntId } }).json()
    return result
  } catch (error) {
    throw error
  }
}


/**
 * 메뉴 목록 조회
 * @param {object} [payload] - 검색 파라미터
 * @returns {Promise<any>}
 */
export const getAllMenuList = async (payload = {}) => {
  try {
    const result = await cmnClient.post('menu-list', { json: payload }).json()
    return result
  } catch (error) {
    throw error
  }
}

const parseFilename = (contentDisposition) => {
  if (!contentDisposition) return 'download'
  const m = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"/i.exec(contentDisposition)
  return decodeURIComponent(m?.[1] || m?.[2] || 'download')
}

/**
 * 파일 다운로드 호출
 * @param {string} atchFileId
 * @param {number} fileSn
 */
export const getFileDownload = async (atchFileId, fileSn) => {
  let res
  try {
    res = await cmmnOnlyClient.get(`file-download/${atchFileId}/${fileSn}`)
  } catch (e) {
    // beforeError 훅에서 이미 serverMessage에 서버 메시지(JSON/text 모두)를 추출해 넣음
    await showAlert(e?.serverMessage || '파일 다운로드 중 오류가 발생하였습니다.')
    return
  }

  // HTTP 200이지만 JSON 오류인 경우 (하위 호환)
  const contentType = res.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    const errorData = await res.json()
    await showAlert(errorData?.message || '파일 다운로드 중 오류가 발생하였습니다.')
    return
  }

  const blob = await res.blob()
  const filename = parseFilename(res.headers.get('content-disposition'))

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/**
 * 파일 Blob URL 생성 (PDF 뷰어 등에서 사용)
 * - BE 프록시 경유로 CORS 우회
 * @param {string} atchFileId
 * @param {number} fileSn
 * @returns {Promise<string>} blob URL
 */
export const getFileBlobUrl = async (atchFileId, fileSn) => {
  let res
  try {
    res = await cmmnOnlyClient.get(`file-download/${atchFileId}/${fileSn}`)
  } catch (e) {
    throw new Error(e?.serverMessage || '파일을 불러오는 중 오류가 발생하였습니다.')
  }

  // HTTP 200이지만 JSON 오류인 경우 (하위 호환)
  const contentType = res.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    const errorData = await res.json()
    throw new Error(errorData?.message || '파일을 불러오는 중 오류가 발생하였습니다.')
  }

  const blob = await res.blob()
  return URL.createObjectURL(blob)
}
 

/**
 * 집계분류 목록 조회
 * @param {object} [payload] - 검색 파라미터
 * @returns {Promise<any>}
 */
export const getCommCateList = async (payload = {}) => {
  try {
    payload.instId = localStorage.getItem('SELECTED_INST') || ''
    const result = await cmnClient.post('cmm-cate-list', { json: payload }).json()
    return result
  } catch (error) {
    throw error
  }
}


/**
 * 집계분류 목록 조회
 * @param {object} [payload] - 검색 파라미터
 * @returns {Promise<any>}
 */
export const getCommAggrCateList = async (payload = {}) => {
  try {
    payload.instId = localStorage.getItem('SELECTED_INST') || ''
    const result = await cmnClient.post('cmm-aggr-cate-list', { json: payload }).json()
    return result
  } catch (error) {
    throw error
  }
}


/**
 * 콘텐츠 조회
 * @param {String} contsId - 콘텐츠ID
 * @returns {Promise<any>}
 */
export const getConts = (contsId) => {
  try {
    return cmnClient.post(`contents`, { json: { contsId } }).json()
  } catch (error) {
    throw error
  }
}


/**
 * 사용자 정보 상세 조회
 * @returns {Promise<any>}
 */
export const getUserInfo = async () => {
  try {
    return await cmnClient.get(`user-info-detail`).json()
  } catch (error) {
    if (error?.response?.status === 404) {
      return await error.response.json()
    }
    throw error
  }
}

/**
 * 결제 요청 
 * @param {object} payload - 결제 요청 파라미터
 * @returns {Promise<any>}
 */
export const preparePayment = (payload) => {
  try {
    return payClient.post(`payment-prepare-proc`, { json: payload }).json()
  } catch (error) {
    throw error
  }
}


/**
 * 환불 요청 
 * @param {object} payload - 환불 요청 파라미터
 * @returns {Promise<any>}
 */
export const cancelPayment = (payload) => {
  try {
    return payClient.post(`payment-cancel-proc`, { json: payload }).json()
  } catch (error) {
    throw error
  }
}


/**
 * 마이페이지 환불은행 목록 조회
 * @returns {Promise<any>}
 */
export const getRefundBankList = () => {
  try {
    return cmnClient.get(`bank-cd-list`).json()
  } catch (error) {
    throw error
  }
}


/**
 * 만족도조사 처리
 * @returns {Promise<any>}
 */
export const procSatis = (payload) => {
  try {
    return cmnClient.post(`satis`, { json: payload }).json()
  } catch (error) {
    throw error
  }
}


/**
 * 메뉴 상세 정보 조회
 * @returns {Promise<any>}
 */
export const getMenuInfoDetail = (payload) => {
  try {
    return cmnClient.post(`menu-info-detail`, { json: payload }).json()
  } catch (error) {
    throw error
  }
}



/**
 * 팝업 조회
 * @returns {Promise<any>}
 */
export const getPopupList = (payload) => {
  try {
    payload = { ...payload, domainId: import.meta.env.VITE_DOMAIN_ID }
    return cmnClient.post(`popup-list`, { json: payload }).json()
  } catch (error) {
    throw error
  }
}


